import { LogLevel, type Configuration } from '@azure/msal-browser';

const PLACEHOLDER_CLIENT_ID = '00000000-0000-0000-0000-000000000000';

export interface ResolvedMsalConfig {
  clientId: string;
  authority: string;
}

/**
 * Read MSAL config from Vite env. Returns null when the placeholder
 * client ID is in effect — the dev shell uses this to render a
 * "not configured" placeholder instead of mounting the MSAL provider.
 */
export function resolveMsalConfig(): ResolvedMsalConfig | null {
  const clientId = import.meta.env.VITE_MSAL_CLIENT_ID;
  const authority =
    import.meta.env.VITE_MSAL_AUTHORITY ?? 'https://login.microsoftonline.com/common';

  if (!clientId || clientId === PLACEHOLDER_CLIENT_ID) return null;

  return { clientId, authority };
}

export function buildMsalConfiguration(config: ResolvedMsalConfig): Configuration {
  // Prefer an explicit VITE_MSAL_REDIRECT_URI when set (production builds
  // bake `https://classy.sm2gk.com`). Fall back to the current origin for
  // local dev so refresh-on-port-change keeps working without a rebuild.
  const redirect = import.meta.env.VITE_MSAL_REDIRECT_URI ?? window.location.origin;
  const postLogout = import.meta.env.VITE_MSAL_POST_LOGOUT_REDIRECT_URI ?? redirect;
  return {
    auth: {
      clientId: config.clientId,
      authority: config.authority,
      redirectUri: redirect,
      postLogoutRedirectUri: postLogout,
      // navigateToLoginRequestUrl removed in MSAL 5 — it was already
      // the effective default of true.
      // storeAuthStateInCookie removed from CacheOptions in MSAL 5 —
      // sessionStorage is the source of truth and no cookie fallback
      // is supported in modern browsers.
    },
    cache: {
      cacheLocation: 'sessionStorage',
    },
    system: {
      loggerOptions: {
        loggerCallback: (level, message, containsPii) => {
          if (containsPii) return;
          if (level === LogLevel.Error) console.error('[msal]', message);
        },
        logLevel: LogLevel.Warning,
      },
    },
  };
}

// Sign-in scopes — Microsoft Graph User.Read gets a profile + an ID token whose
// `aud` is the SPA's own clientId, which the API validates as the bearer.
// Matches the LofiBooks/Amaze pattern: no separate API app registration, no
// "Expose an API" scope. See docs/LOCAL_DEV.md.
export function apiScopes(_config: ResolvedMsalConfig): string[] {
  return ['User.Read'];
}
